import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, MapPin, Briefcase, Calendar, Building2, ExternalLink } from "lucide-react";
import { safeUrl } from "../lib/safeUrl";
import EmployerLogo from "../components/EmployerLogo";
import VacancyPoster from "../components/VacancyPoster";
import { useAuth } from "../lib/AuthContext";
import { regionFromLocation } from "../lib/regions";

export default function VacancyDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/vacancies/${id}`).then((r) => setV(r.data)).catch(() => setNotFound(true));
  }, [id]);

  // JobPosting structured data — material for Google for Jobs / SEO
  useEffect(() => {
    if (!v) return;
    const salaryStr = v.salary_min && v.salary_max ? `£${v.salary_min.toLocaleString()}–£${v.salary_max.toLocaleString()}` : v.salary_min ? `from £${v.salary_min.toLocaleString()}` : "Salary on application";
    const metaDesc = `${v.employer_name} is hiring a ${v.title} in ${v.location || "the UK"}. ${salaryStr}. Apply directly via Railway Careers UK — no recruiters, no fees.`;
    const desc = document.head.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    desc.setAttribute("content", metaDesc);

    const ld = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: v.title,
      description: v.description,
      datePosted: v.created_at,
      ...(v.expiry_date ? { validThrough: `${v.expiry_date}T23:59:00+00:00` } : {}),
      employmentType: (v.employment_type || "full-time").toUpperCase().replace("-", "_"),
      hiringOrganization: { "@type": "Organization", name: v.employer_name, sameAs: undefined },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressLocality: v.location,
          addressCountry: "GB",
          ...(v.region ? { addressRegion: v.region } : {}),
        },
        ...(v.lat && v.lng ? { geo: { "@type": "GeoCoordinates", latitude: v.lat, longitude: v.lng } } : {}),
      },
      ...(v.salary_min ? {
        baseSalary: {
          "@type": "MonetaryAmount", currency: "GBP",
          value: { "@type": "QuantitativeValue", minValue: v.salary_min, maxValue: v.salary_max || v.salary_min, unitText: "YEAR" },
        },
      } : {}),
      ...(v.apply_url ? { directApply: false, url: v.apply_url } : {}),
      industry: "Rail Transport",
      occupationalCategory: v.role_category,
    };
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.text = JSON.stringify(ld);
    tag.id = "jobposting-ld";
    document.querySelectorAll("#jobposting-ld").forEach((n) => n.remove());
    document.head.appendChild(tag);
    document.title = `${v.title} — ${v.employer_name} | Railway Careers UK`;
    return () => { tag.remove(); };
  }, [v]);

  if (notFound) return <div className="max-w-3xl mx-auto p-12 text-center"><h1 className="font-display text-2xl">Vacancy not found</h1><Link to="/vacancies" className="text-amber-600 mt-4 inline-block">← Back to vacancies</Link></div>;
  if (!v) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;

  const salary = v.salary_min && v.salary_max ? `£${v.salary_min.toLocaleString()} – £${v.salary_max.toLocaleString()}` : v.salary_min ? `From £${v.salary_min.toLocaleString()}` : "Salary on application";

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-10 pb-20">
        <Link to="/vacancies" className="inline-flex items-center gap-2 text-sm text-ink hover:text-amber-600 mb-6" data-testid="vac-back">
          <ArrowLeft className="w-4 h-4" /> All vacancies
        </Link>
        <div className={`border ${v.featured ? "border-amber border-l-4 bg-featured" : "border-border bg-card"} rounded-lg p-8 md:p-10`}>
          <div className="flex items-start gap-5 mb-4">
            <EmployerLogo name={v.employer_name} override={v.employer_logo} size={64} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {v.featured && <span className="px-2 py-0.5 bg-amber text-ink text-[10px] font-bold uppercase tracking-wider rounded">Featured</span>}
                <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">{v.role_category.replace("-", " ")}</span>
              </div>
              <h1 className="font-display font-extrabold text-ink text-3xl md:text-5xl tracking-tight leading-tight mt-2">{v.title}</h1>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4" /> {v.employer_name}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {v.location}</span>
            <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {v.employment_type}</span>
            {v.closing_date && <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Closes {v.closing_date}</span>}
          </div>
          <div className="mt-6 inline-block bg-navy text-amber font-mono text-sm px-4 py-2 rounded">{salary}</div>

          {/* Quick Facts — LLM-citable structured block */}
          <dl className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border border-border rounded-lg p-5 bg-muted/40" data-testid="vacancy-quick-facts">
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Salary</dt>
              <dd className="font-mono text-sm text-ink mt-1">{salary}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Location</dt>
              <dd className="font-mono text-sm text-ink mt-1">{v.location || "United Kingdom"}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Type</dt>
              <dd className="font-mono text-sm text-ink mt-1 capitalize">{(v.employment_type || "full-time").replace("-", " ")}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Closes</dt>
              <dd className="font-mono text-sm text-ink mt-1">{v.closing_date || v.expiry_date || "Open until filled"}</dd>
            </div>
          </dl>

          <div className="mt-10 prose prose-slate max-w-none">
            <h2 className="font-display text-xl font-bold text-ink">Role overview</h2>
            <p className="whitespace-pre-line text-muted-foreground leading-relaxed">{v.description}</p>
            {v.requirements && (
              <>
                <h2 className="font-display text-xl font-bold text-ink mt-8">Requirements</h2>
                <p className="whitespace-pre-line text-muted-foreground leading-relaxed">{v.requirements}</p>
              </>
            )}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            {v.apply_url && (
              <Button asChild size="lg" className="bg-navy text-bone hover:bg-lime-600 hover:text-ink font-semibold" data-testid="vac-apply">
                <a
                  href={safeUrl(v.apply_url)}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  onClick={() => { api.post(`/vacancies/${v.id}/click`).catch(() => {}); }}
                >
                  Apply with employer <ExternalLink className="ml-2 w-4 h-4" />
                </a>
              </Button>
            )}
            <Button asChild size="lg" variant="outline" className="border-white text-ink">
              <Link to={`/careers/${v.role_category}`}>Read role guide</Link>
            </Button>
            {v.employer_name && (
              <Button asChild size="lg" variant="outline" className="border-white text-ink" data-testid="vac-employer-link">
                <Link to={`/operators/${v.employer_name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                  More from {v.employer_name}
                </Link>
              </Button>
            )}
            {(() => {
              const reg = regionFromLocation(`${v.location || ""} ${v.region || ""}`);
              if (!reg) return null;
              return (
                <Button asChild size="lg" variant="outline" className="border-white text-ink" data-testid="vac-region-link">
                  <Link to={`/jobs/${reg.slug}`}>More jobs in {reg.name}</Link>
                </Button>
              );
            })()}
            {v.role_category === "train-driver" && (
              <Button asChild size="lg" variant="outline" className="border-amber text-amber-700 hover:bg-amber hover:text-ink" data-testid="vac-cornerstone-link">
                <Link to="/train-driver-jobs">UK train driver guide</Link>
              </Button>
            )}
          </div>

          {user && (user.role === "admin" || user.id === v.employer_id) && (
            <div className="mt-8 pt-6 border-t border-line">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono mb-3">
                Owner tools
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <VacancyPoster vacancy={v} />
                <span className="text-[12px] text-muted-foreground max-w-md">
                  1080×1350 PNG. Cyan/navy poster for socials, station notice boards or printed A4.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
